import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Search, AlertTriangle, Trash2, FileSpreadsheet, FileDown, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { ekstreIndir } from '@/lib/ekstre';
import { HESAP_TURLERI, YENI_HESAP_TURLERI, VARSAYILAN_HESAP_TURU } from '@/lib/hesapTurleri';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { COUNTRIES_SORTED, getPhoneFormat, getPhonePrefix } from '@/lib/countries';

const fmtTR = (n, d = 2) =>
  n == null ? '—' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });

// Tür listesi tek yerde (lib/hesapTurleri.js); yeni hesapta Personel yok —
// personel carisi İK'dan açılır.
const ACCOUNT_TYPES = HESAP_TURLERI;
const ACCOUNT_TYPES_NEW = YENI_HESAP_TURLERI;

// SÜTUN SIRALAMASI (kullanıcı, 2026-10-07): sayısal sütun ilk tıklamada
// BÜYÜKTEN KÜÇÜĞE, metin sütunu A'dan Z'ye; ikinci tıklama yönü çevirir.
// Liste sayfalı değil (bütün cariler bellekte), sıralama istemcide doğru.
const SIRALAMA_SUTUNLARI = {
  code: { tur: 'metin' },
  name: { tur: 'metin' },
  account_type: { tur: 'metin' },
  currency: { tur: 'metin' },
  total_debt: { tur: 'sayi' },
  total_payment: { tur: 'sayi' },
  balance: { tur: 'sayi' },
  upcoming_debt: { tur: 'sayi' },
  overdue_debt: { tur: 'sayi' },
};

// Bir cari birden çok para birimi satırı taşıyabilir ve satırlar GRUP olarak
// sıralanır (alt satırlar carisinden ayrılmasın). Farklı para birimlerini
// toplamak anlamsız olduğu için grubun sayısal anahtarı ÜSTTEKİ satırdır —
// ekranda görünen rakamla sıranın tutması için; üst satır TRY'dir (bkz.
// `pbSirasi`). PB süzgeci seçiliyse grupta zaten tek satır kalır.
const grupDegeri = (g, alan) => {
  if (alan === 'currency') return g.currencies.map(c => c.currency).join(',');
  if (!SIRALAMA_SUTUNLARI[alan] || SIRALAMA_SUTUNLARI[alan].tur !== 'sayi') return g[alan] || '';
  return Number(g.currencies[0]?.[alan] || 0);
};

// TRY başta, kalanlar alfabetik (Kasa/Banka listesiyle aynı sıra). Sunucu
// para birimlerini bir kümeden üretiyor, sıra her açılışta değişebiliyordu.
const pbSirasi = (a, b) => (a.currency === 'TRY' ? -1 : b.currency === 'TRY' ? 1 : a.currency.localeCompare(b.currency));

export default function SupplierList() {
  const { company } = useAuth();
  const defaultCountry = company?.address_country || 'TR';
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [currFilter, setCurrFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showNew, setShowNew] = useState(false);
  const emptyForm = {
    name: '', address_street: '', address_district: '', address_city: '', address_country: defaultCountry,
    email: '', phone_country: defaultCountry, phone: '', auth_phone_country: defaultCountry, authorized_phone: '',
    tax_office: '', tax_number: '', account_type: VARSAYILAN_HESAP_TURU
  };
  const [form, setForm] = useState({ ...emptyForm });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchSuppliers(); }, []);
  const fetchSuppliers = () => api.get('/suppliers').then(r => setSuppliers(r.data)).catch(() => {});

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      await api.post('/suppliers', {
        name: form.name,
        address_street: form.address_street, address_district: form.address_district,
        address_city: form.address_city, address_country: form.address_country,
        email: form.email,
        phone: form.phone ? `${getPhonePrefix(form.phone_country)} ${form.phone}` : '',
        authorized_phone: form.authorized_phone ? `${getPhonePrefix(form.auth_phone_country)} ${form.authorized_phone}` : '',
        tax_office: form.tax_office, tax_number: form.tax_number,
        account_type: form.account_type,
        bank_accounts: bankAccounts.filter(b => b.bank_name.trim()).map(b => ({
          id: b.id, bank_name: b.bank_name, currency: b.currency,
          iban: b.iban || null, uban: b.uban || null,
          account_holder: b.account_holder || '', is_active: true
        }))
      });
      toast.success('Cari hesap olusturuldu');
      setShowNew(false);
      setForm({ ...emptyForm });
      setBankAccounts([]);
      fetchSuppliers();
    } catch (err) { toast.error('Hata olustu'); }
    setLoading(false);
  };

  const trLower = str => str.replace(/İ/g, 'i').replace(/I/g, 'ı').toLowerCase();
  const filtered = suppliers.filter(s => {
    if (search) {
      const sl = trLower(search);
      if (!trLower(s.name).includes(sl) && !trLower(s.code || '').includes(sl)) return false;
    }
    if (currFilter !== 'all' && s.currency !== currFilter) return false;
    if (typeFilter !== 'all' && s.account_type !== typeFilter) return false;
    return true;
  });

  // Cari hesapları grupla - aynı cari hesabın farklı para birimleri alt satır olarak görünsün
  const groupedSuppliers = filtered.reduce((acc, s) => {
    const key = s.supplier_id || s.id;
    if (!acc[key]) {
      acc[key] = {
        id: s.supplier_id || s.id,
        name: s.name,
        code: s.code,
        account_type: s.account_type,
        currencies: []
      };
    }
    acc[key].currencies.push({
      currency: s.currency || 'TRY',
      total_debt: s.total_debt || 0,
      total_payment: s.total_payment || 0,
      balance: s.net_balance ?? s.balance ?? 0,
      upcoming_debt: s.upcoming_debt || 0,
      overdue_debt: s.overdue_debt || 0
    });
    return acc;
  }, {});

  const [siralama, setSiralama] = useState({ alan: null, yon: 'asc' });
  const sirala = (alan) => setSiralama(o => {
    const varsayilan = SIRALAMA_SUTUNLARI[alan].tur === 'sayi' ? 'desc' : 'asc';
    if (o.alan !== alan) return { alan, yon: varsayilan };
    return { alan, yon: o.yon === 'asc' ? 'desc' : 'asc' };
  });

  const groupedList = (() => {
    const liste = Object.values(groupedSuppliers);
    liste.forEach(g => g.currencies.sort(pbSirasi));
    if (!siralama.alan) return liste;
    const isaret = siralama.yon === 'asc' ? 1 : -1;
    const sayisal = SIRALAMA_SUTUNLARI[siralama.alan].tur === 'sayi';
    // Metin Türkçe alfabeyle (Ş/Ç/İ sona düşmesin); kod "320.010" gibi sayı
    // içerdiği için `numeric` — yoksa 320.010, 320.002'den önce gelirdi.
    return [...liste].sort((a, b) => {
      const x = grupDegeri(a, siralama.alan), y = grupDegeri(b, siralama.alan);
      // Boş metin (kodu olmayan cari) yön ne olursa olsun SONDA
      if (!sayisal && !x !== !y) return !x ? 1 : -1;
      const fark = sayisal ? x - y : String(x).localeCompare(String(y), 'tr', { numeric: true, sensitivity: 'base' });
      return fark * isaret || (a.name || '').localeCompare(b.name || '', 'tr');
    });
  })();

  const baslik = (alan, children, sag) => {
    const aktif = siralama.alan === alan;
    const Ok = !aktif ? ArrowUpDown : siralama.yon === 'asc' ? ArrowUp : ArrowDown;
    return (
      <TableHead key={alan} className={`text-[#1a1916] font-bold text-xs uppercase ${sag ? 'text-right' : ''}`}>
        <button type="button" onClick={() => sirala(alan)} data-testid={`sirala-${alan}`}
          className={`inline-flex items-center gap-1 uppercase hover:text-[#3d6e4e] ${sag ? 'flex-row-reverse' : ''}`}
          title={SIRALAMA_SUTUNLARI[alan].tur === 'sayi' ? 'Sırala (önce büyükten küçüğe)' : "Sırala (önce A'dan Z'ye)"}>
          {children}
          <Ok size={12} className={aktif ? 'text-[#3d6e4e]' : 'text-[#b5aa98]'} />
        </button>
      </TableHead>
    );
  };

  // Döküm EKRANDAKİ süzgeçle sınırlı: "Personel'i filtreledim, export edince
  // sadece personeller gelsin" (kullanıcı, 2026-09-20). Görünen satırların
  // anahtarları gönderilir; rakamları sunucu kendisi hesaplar.
  const listeDokumu = (bicim) => ekstreIndir({
    yol: '/suppliers/liste-dokumu',
    bicim,
    satirIdler: filtered.map(s => `${s.id}|${s.currency || 'TRY'}`),
    filtre: [
      `Hesap türü: ${typeFilter === 'all' ? 'Tümü' : typeFilter}`,
      `Para birimi: ${currFilter === 'all' ? 'Tümü' : currFilter}`,
      search ? `Arama: ${search}` : null,
    ].filter(Boolean).join(' · '),
    ad: typeFilter === 'all' ? 'Cari-Hesaplar' : typeFilter,
    bosMesaj: 'Süzgece uyan cari hesap yok',
    tur: 'listesi',
  });

  return (
    <div data-testid="supplier-list-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Cari Hesaplar</h1>
          <p className="text-sm text-[#8a7e70] mt-1">{filtered.length} kayit</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => listeDokumu('xlsx')}
            className="border-[#d0c4b0] text-[#3d6e4e] hover:bg-[#3d6e4e]/10"
            title="Ekrandaki listeyi Excel olarak indir" data-testid="liste-excel">
            <FileSpreadsheet size={16} className="mr-2" /> Excel
          </Button>
          <Button variant="outline" onClick={() => listeDokumu('pdf')}
            className="border-[#d0c4b0] text-[#7a3535] hover:bg-[#7a3535]/10"
            title="Ekrandaki listeyi PDF olarak indir" data-testid="liste-pdf">
            <FileDown size={16} className="mr-2" /> PDF
          </Button>
          <Button onClick={() => setShowNew(true)} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-supplier-btn">
            <Plus size={16} className="mr-2" /> Yeni Cari Hesap
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input placeholder="Cari hesap ara..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white border-[#d0c4b0]" data-testid="supplier-search" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-52 bg-white border-[#d0c4b0]" data-testid="account-type-filter">
            <SelectValue placeholder="Hesap Turu" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Turler</SelectItem>
            {ACCOUNT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={currFilter} onValueChange={setCurrFilter}>
          <SelectTrigger className="w-32 bg-white border-[#d0c4b0]" data-testid="currency-filter">
            <SelectValue placeholder="Para Birimi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tumu</SelectItem>
            <SelectItem value="TRY">TRY</SelectItem>
            <SelectItem value="USD">USD</SelectItem>
            <SelectItem value="EUR">EUR</SelectItem>
            <SelectItem value="GBP">GBP</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              {baslik('code', 'Kod')}
              {baslik('name', 'Cari Hesap Adi')}
              {baslik('account_type', 'Hesap Turu')}
              {baslik('currency', 'PB')}
              {baslik('total_debt', 'Toplam Borc', true)}
              {baslik('total_payment', 'Toplam Odeme', true)}
              {baslik('balance', 'Net Bakiye', true)}
              {/* `upcoming_debt` vadesi HENÜZ gelmemiş borçtur — başlık bir dönem
                  "Vadesi Gelmis" diyordu. "Vadesi Gelen" bugün dahil; cari detayındaki
                  kutuyla aynı sınır (bkz. purchasing.list_suppliers). */}
              {baslik('upcoming_debt', 'Vadesi Gelmemiş', true)}
              {baslik('overdue_debt', 'Vadesi Gelen', true)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedList.map((supplier) => {
              const hasMultipleCurrencies = supplier.currencies.length > 1;
              return supplier.currencies.map((curr, currIdx) => {
                const hasOverdue = curr.overdue_debt > 0;
                const isFirst = currIdx === 0;
                return (
                  <TableRow 
                    key={`${supplier.id}-${curr.currency}`} 
                    className={`erp-table-row border-[#d0c4b0] ${hasOverdue ? 'bg-[#7a3535]/5' : ''} ${!isFirst ? 'bg-[#f5f0e8]/50' : ''}`}
                  >
                    {/* Kod - sadece ilk satırda */}
                    <TableCell className="font-mono text-sm text-[#8a7e70]">
                      {isFirst ? (supplier.code || '-') : ''}
                    </TableCell>
                    {/* Cari Hesap Adı - sadece ilk satırda */}
                    <TableCell>
                      {isFirst ? (
                        <Link to={`/erp/suppliers/${supplier.id}`} className="font-medium text-[#1a1916] hover:underline" data-testid={`supplier-link-${supplier.id}`}>
                          {supplier.name}
                        </Link>
                      ) : (
                        <span className="text-xs text-[#8a7e70] pl-4">└ {curr.currency} bakiye</span>
                      )}
                    </TableCell>
                    {/* Hesap Türü - sadece ilk satırda */}
                    <TableCell className="text-xs text-[#8a7e70]">
                      {isFirst ? (supplier.account_type || '-') : ''}
                    </TableCell>
                    {/* Para Birimi */}
                    <TableCell className="text-sm font-semibold text-[#3F3E39]">{curr.currency}</TableCell>
                    {/* Finansal Değerler */}
                    <TableCell className="text-right font-mono text-sm text-[#7a3535]">{fmtTR(curr.total_debt)}</TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#3d6e4e]">{fmtTR(curr.total_payment)}</TableCell>
                    <TableCell className={`text-right font-mono text-sm font-bold ${curr.balance < 0 ? 'text-[#7a3535]' : curr.balance > 0 ? 'text-[#3d6e4e]' : 'text-[#8a7e70]'}`}>
                      {fmtTR(curr.balance)}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm text-[#8a7e70]">{fmtTR(curr.upcoming_debt)}</TableCell>
                    <TableCell className="text-right">
                      {hasOverdue && <AlertTriangle size={12} className="inline mr-1 text-[#7a3535]" />}
                      <span className={`font-mono text-sm ${hasOverdue ? 'text-[#7a3535] font-bold' : 'text-[#8a7e70]'}`}>
                        {fmtTR(curr.overdue_debt)}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              });
            })}
            {groupedList.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#8a7e70]">Cari hesap bulunamadi</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* New Account Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-[#1a1916] font-bold">Yeni Cari Hesap</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Unvan *</Label>
                <Input value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" data-testid="supplier-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Hesap Turu *</Label>
                <Select value={form.account_type} onValueChange={(v) => setForm(p => ({ ...p, account_type: v }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="account-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCOUNT_TYPES_NEW.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Vergi Dairesi</Label>
                <Input value={form.tax_office} onChange={(e) => setForm(p => ({ ...p, tax_office: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" data-testid="supplier-tax-office" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Vergi No</Label>
                <Input value={form.tax_number} onChange={(e) => setForm(p => ({ ...p, tax_number: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" data-testid="supplier-tax-number" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Telefon</Label>
                <div className="flex gap-2">
                  <Select value={form.phone_country} onValueChange={(v) => setForm(p => ({ ...p, phone_country: v }))}>
                    <SelectTrigger className="w-28 bg-white border-[#d0c4b0] text-xs" data-testid="supplier-phone-country"><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES_SORTED.map(c => <SelectItem key={c.code} value={c.code}>{c.phone} {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))}
                    placeholder={getPhoneFormat(form.phone_country)} className="flex-1 bg-white border-[#d0c4b0]" data-testid="supplier-phone" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Yetkili Telefon</Label>
                <div className="flex gap-2">
                  <Select value={form.auth_phone_country} onValueChange={(v) => setForm(p => ({ ...p, auth_phone_country: v }))}>
                    <SelectTrigger className="w-28 bg-white border-[#d0c4b0] text-xs" data-testid="supplier-auth-phone-country"><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES_SORTED.map(c => <SelectItem key={c.code} value={c.code}>{c.phone} {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={form.authorized_phone} onChange={(e) => setForm(p => ({ ...p, authorized_phone: e.target.value }))}
                    placeholder={getPhoneFormat(form.auth_phone_country)} className="flex-1 bg-white border-[#d0c4b0]" data-testid="supplier-auth-phone" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">E-posta</Label>
              <Input value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                className="bg-white border-[#d0c4b0]" data-testid="supplier-email" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39]">Adres</Label>
              <Input value={form.address_street} onChange={(e) => setForm(p => ({ ...p, address_street: e.target.value }))}
                placeholder="Sokak / Cadde / Bina No" className="bg-white border-[#d0c4b0]" data-testid="supplier-address-street" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Ilce</Label>
                <Input value={form.address_district} onChange={(e) => setForm(p => ({ ...p, address_district: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" data-testid="supplier-district" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Il</Label>
                <Input value={form.address_city} onChange={(e) => setForm(p => ({ ...p, address_city: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" data-testid="supplier-city" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#3F3E39]">Ulke</Label>
                <Select value={form.address_country} onValueChange={(v) => setForm(p => ({ ...p, address_country: v }))}>
                  <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="supplier-country"><SelectValue /></SelectTrigger>
                  <SelectContent>{COUNTRIES_SORTED.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Bank Accounts */}
            <div className="border-t border-[#d0c4b0] pt-4 mt-2">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-[#3F3E39] font-semibold">Banka Hesaplari</Label>
                <Button type="button" size="sm" variant="outline"
                  onClick={() => setBankAccounts(prev => [...prev, { id: `new-${Date.now()}`, bank_name: '', currency: 'TRY', iban: '', account_holder: '' }])}
                  className="border-[#d0c4b0] text-[#3F3E39] h-7 text-xs" data-testid="add-bank-inline">
                  <Plus size={12} className="mr-1" /> Hesap Ekle
                </Button>
              </div>
              {bankAccounts.map((ba, i) => (
                <div key={ba.id} className="grid grid-cols-12 gap-2 mb-2 items-end">
                  <div className="col-span-3">
                    {i === 0 && <p className="text-xs text-[#8a7e70] mb-1">Banka</p>}
                    <Input value={ba.bank_name} placeholder="Banka adi"
                      onChange={(e) => { const u = [...bankAccounts]; u[i] = { ...u[i], bank_name: e.target.value }; setBankAccounts(u); }}
                      className="bg-white border-[#d0c4b0] text-sm h-9" data-testid={`bank-name-${i}`} />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <p className="text-xs text-[#8a7e70] mb-1">PB</p>}
                    <Select value={ba.currency} onValueChange={(v) => { const u = [...bankAccounts]; u[i] = { ...u[i], currency: v }; setBankAccounts(u); }}>
                      <SelectTrigger className="bg-white border-[#d0c4b0] text-sm h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="TRY">TRY</SelectItem>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    {i === 0 && <p className="text-xs text-[#8a7e70] mb-1">IBAN</p>}
                    <Input value={ba.iban} placeholder="TR..."
                      onChange={(e) => { const u = [...bankAccounts]; u[i] = { ...u[i], iban: e.target.value }; setBankAccounts(u); }}
                      className="bg-white border-[#d0c4b0] text-sm h-9 font-mono" data-testid={`bank-iban-${i}`} />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <p className="text-xs text-[#8a7e70] mb-1">Hesap Sahibi</p>}
                    <Input value={ba.account_holder} placeholder="Ad Soyad"
                      onChange={(e) => { const u = [...bankAccounts]; u[i] = { ...u[i], account_holder: e.target.value }; setBankAccounts(u); }}
                      className="bg-white border-[#d0c4b0] text-sm h-9" data-testid={`bank-holder-${i}`} />
                  </div>
                  <div className="col-span-1">
                    <Button type="button" variant="ghost" size="icon"
                      onClick={() => setBankAccounts(prev => prev.filter((_, j) => j !== i))}
                      className="h-9 w-9 text-[#8a7e70] hover:text-[#7a3535]" data-testid={`bank-remove-${i}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              ))}
              {bankAccounts.length === 0 && (
                <p className="text-xs text-[#8a7e70] text-center py-2">Henuz banka hesabi eklenmedi</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} className="border-[#d0c4b0]">Iptal</Button>
            <Button onClick={handleCreate} disabled={loading}
              className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="save-supplier-btn">
              {loading ? 'Kaydediliyor...' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
